import { VIZIT_ENV } from '@vizit/constants';
import type { PlainInstance, PlainThreadDetails } from './types';

const PLAIN_SCRIPT_SRC = 'https://chat.cdn-plain.com/index.js';

/** The chat script keeps its state (the conversation session, unread engagement) under this localStorage prefix. */
const PLAIN_STORAGE_PREFIX = 'plain:';
/** Plain thread field that carries the user's organization, as Help Scout's company did. */
const ORGANIZATION_NAME_FIELD = 'organization_name';

/**
 * The panel is 420px wide and Plain only positions it from the right, so this pins its left edge 8px past the
 * 52px nav rail (in line with the launcher) and keeps it on screen in narrow windows.
 */
const PANEL_RIGHT = 'max(16px, calc(100vw - 60px - 420px))';
/** Clears the launcher: 20px from the bottom, 40px tall, then a 16px gap. */
const PANEL_BOTTOM = '76px';

let loading: Promise<PlainInstance> | null = null;
let initializedUserId: string | null = null;
// Plain can't be torn down in-page, so after a sign-out it stays off until the next page load rather than risk
// showing the previous person's conversations to whoever signs in next.
let isStale = false;
// Bumped on every sign-out, so a start still waiting on the script knows its user has gone by the time it loads.
let signOutCount = 0;

/** Injects the chat script once; resolves with the global `Plain` object. */
function loadPlain(): Promise<PlainInstance> {
  if (window.Plain) return Promise.resolve(window.Plain);
  if (!loading) {
    loading = new Promise<PlainInstance>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PLAIN_SCRIPT_SRC;
      script.async = true;
      script.onload = () => {
        if (window.Plain) {
          resolve(window.Plain);
          return;
        }
        loading = null;
        script.remove();
        reject(new Error('The Plain chat script loaded without defining window.Plain'));
      };
      script.onerror = () => {
        loading = null;
        script.remove();
        reject(new Error('The Plain chat script failed to load'));
      };
      document.head.append(script);
    });
  }
  return loading;
}

interface PlainUser {
  id: string;
  email: string;
  name?: string;
  organizationName?: string;
}

/**
 * Loads and initializes the chat for the signed-in user, with Plain's own launcher hidden. Resolves to `false`
 * (without loading anything) when the chat has gone stale after a sign-out.
 */
async function startPlainChat(appId: string, user: PlainUser): Promise<boolean> {
  if (isStale || (initializedUserId != null && initializedUserId !== user.id)) return false;

  const startedAt = signOutCount;
  const plain = await loadPlain();
  if (signOutCount !== startedAt) return false;
  // Same environment prefix as Pendo, so dev and staging users never collide with production ones in Plain.
  const externalId = VIZIT_ENV !== 'prod' ? `${VIZIT_ENV}-${user.id}` : user.id;
  // Without a name Plain greets the visitor generically, which reads better than greeting them by email address.
  const customerDetails = { email: user.email, externalId, ...(user.name ? { fullName: user.name } : {}) };
  // Plain's customer has no company field, so support sees the organization on each thread instead.
  const threadDetails: PlainThreadDetails = user.organizationName
    ? { threadFields: [{ key: ORGANIZATION_NAME_FIELD, type: 'STRING', stringValue: user.organizationName }] }
    : {};

  if (plain.isInitialized()) {
    // `update` replaces top-level fields, so passing threadDetails also drops an organization the user switched away from.
    plain.update({ customerDetails, threadDetails });
  } else {
    plain.init({
      appId,
      hideLauncher: true,
      position: { right: PANEL_RIGHT, bottom: PANEL_BOTTOM },
      customerDetails,
      threadDetails,
    });
  }
  initializedUserId = user.id;
  return true;
}

/** Closes the chat and forgets everything the chat script stored for the visitor, for sign-out. */
function clearPlainSession() {
  window.Plain?.close();
  try {
    const plainKeys = Object.keys(localStorage).filter((key) => key.startsWith(PLAIN_STORAGE_PREFIX));
    for (const key of plainKeys) localStorage.removeItem(key);
  } catch {
    // Storage can be unavailable (private mode, blocked site data); there is then no session to clear.
  }
  if (initializedUserId != null) isStale = true;
  signOutCount += 1;
}

export { clearPlainSession, startPlainChat };
export type { PlainUser };
